import {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
  type FormEvent
} from "react";
import { useTranslator } from "@eo-locale/react";

import trim from "lodash/trim";

import {
  Field,
  SingleLineQuestion,
  ColourOrImageQuestion,
  CommonNumberQuestion,
  CommonDropdownQuestion,
  RichTextEditor
} from "@/storybook-components";

import RequiredField from "@/components/questions/required-field";
import useUser from "@/hooks/useUser";
import { getPrivateFileDownloadUrl } from "@/helpers/private-file-url";
import { usePrivateFileRecordImageUrl } from "@/hooks/use-private-file-display-url";
import { useAppSelector } from "@/redux/store/hooks";
import PreviewCard from "../../common/preview-card";
import {
  getPlainTextLengthFromHtml,
  preserveTrailingSpacesInHtml,
  scrollIntoView,
  stripEditorHtml
} from "@/lib";

import Alerts from "@/components/alerts";
import type { IdeaPostData, IdeaQuestionError } from "@/types";

interface CreateIdeaProps {
  postData: IdeaPostData;
  setPostData: Dispatch<SetStateAction<IdeaPostData>>;
  questionError: IdeaQuestionError;
  setQuestionError: Dispatch<SetStateAction<IdeaQuestionError>>;
  hasErrorInSubmission: boolean;
  ideaSubmitting: any;
  isEditPost: boolean;
  currentPostData?: any;
  onSubmit?: (e?: FormEvent) => any;
}

const DESCRIPTION_ERROR_OVER_LIMIT = "overLimit";

const isDescriptionOverLimit = (html: string, limit: number): boolean =>
  limit > 0 && getPlainTextLengthFromHtml(html) > limit;

const CreateIdea = ({
  postData,
  setPostData,
  questionError,
  setQuestionError,
  hasErrorInSubmission,
  ideaSubmitting,
  isEditPost,
  currentPostData,
  onSubmit
}: CreateIdeaProps): JSX.Element => {
  const { translate } = useTranslator();
  const { isRegisteredUserLoggedIn, isUserAdmin } = useUser();
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const { registeredUser, unverifiedUser } = useAppSelector(
    (state) => state.user
  );
  const {
    layout,
    allowCommentsForIdeas,
    allowFileUploadForIdeas,
    toolConfigurationAttributes
  } = useAppSelector((state) => state.brainstormers.brainstormer);
  const limitAdditionalDetailsLength =
    toolConfigurationAttributes?.preferences?.limitAdditionalDetailsLength ||
    5000;
  const { locale } = useAppSelector((state) => state.site.site);
  const [hasBackgroundColorNote, setHasBackgroundColorNote] = useState(true);

  const categories = useAppSelector((state) => state.brainstormers.categories);

  const displayExistingImageUrl = usePrivateFileRecordImageUrl({
    record: isEditPost ? currentPostData : undefined,
    tool: "ideas"
  });

  useEffect(() => {
    if (isEditPost) {
      setPostData({
        ...postData,
        name: currentPostData?.name,
        description: currentPostData?.description,
        mediaUrl:
          getPrivateFileDownloadUrl(currentPostData, "ideas") ??
          currentPostData?.mediaUrl,
        backgroundColourId: currentPostData?.backgroundColourId,
        categoryId: currentPostData?.categoryId || null
      });
    }
  }, []);

  useEffect(() => {
    if (!limitAdditionalDetailsLength) {
      setQuestionError((prev) =>
        prev.description ? { ...prev, description: "" } : prev
      );
      return;
    }
    const desc = postData?.description ?? "";
    const nextError = isDescriptionOverLimit(desc, limitAdditionalDetailsLength)
      ? DESCRIPTION_ERROR_OVER_LIMIT
      : "";
    setQuestionError((prev) =>
      prev.description === nextError
        ? prev
        : { ...prev, description: nextError }
    );
  }, [limitAdditionalDetailsLength, postData?.description]);

  const handleTitleChange = (text: string) => {
    const trimmedText = trim(text);
    setPostData({ ...postData, name: trimmedText });
    setQuestionError({ ...questionError, name: "" });

    if (trimmedText === "") {
      setQuestionError({ ...questionError, name: "blank" });
    }
  };

  const visibleCategories = categories.filter((cat) => cat?.visible);
  const categoryOptions = visibleCategories.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));
  const selectedCategory =
    categories
      .filter((cat) => String(cat.id) === String(postData?.categoryId))
      .map((cat) => ({ value: cat.id, label: cat.name }))[0] || null;

  const handleCategoryChange = (
    selected: { value: string; label: string } | null
  ) => {
    setPostData({ ...postData, categoryId: selected?.value || null });
  };
  const validateAndSubmit = async (e?: FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Validate required fields
    let hasErrors = false;
    const newErrors = { ...questionError };

    // Validate title/name
    if (!postData?.name || trim(postData.name) === "") {
      newErrors.name = "blank";
      hasErrors = true;
    }

    if (
      isDescriptionOverLimit(
        postData?.description || "",
        limitAdditionalDetailsLength
      )
    ) {
      newErrors.description = DESCRIPTION_ERROR_OVER_LIMIT;
      hasErrors = true;
    }

    // Validate style (background color or image)
    if (!postData?.backgroundColourId && !postData?.mediaUrl) {
      newErrors.backgroundColourId = "empty";
      newErrors.mediaUrl = "empty";
      hasErrors = true;
    }

    if (
      layout === "BudgetAllocator" &&
      !postData?.baItemConfigAttributes?.unitCap?.trim()
    ) {
      newErrors.baItemConfigAttributes = { unitCap: "blank" };
      hasErrors = true;
    }

    // Update errors
    setQuestionError(newErrors);

    // If there are errors, scroll to the first error and focus
    if (hasErrors) {
      // Use setTimeout to ensure DOM has updated with error classes
      setTimeout(() => {
        const errorElement = document.querySelector(".ehq-question-error");
        if (errorElement) {
          // Focus on the first input, button, or textarea within the error element
          const focusableElement = errorElement.querySelector(
            "input, button, textarea, select"
          ) as HTMLElement;

          if (focusableElement) {
            focusableElement.focus();
          }

          // Scroll to the error element
          scrollIntoView(".ehq-question-error");
        }
      }, 100);
      return false;
    }

    // If no errors and onSubmit is provided, call it
    if (onSubmit) {
      return onSubmit(e);
    }

    return true;
  };

  const previewNode = () => {
    const userName = registeredUser?.profile.login || unverifiedUser?.login;
    if (
      postData?.name &&
      (postData.backgroundColourId || postData.mediaUrl) &&
      (isRegisteredUserLoggedIn || userName)
    ) {
      return (
        <PreviewCard
          data={postData}
          username={userName}
          avatarURL={registeredUser?.profile?.imageUrl}
          showCommentIcon={allowCommentsForIdeas}
        />
      );
    }
  };

  const budgetAllocatorAnswer = postData?.baItemConfigAttributes?.unitCap
    ? Intl.NumberFormat(locale).format(
        postData?.baItemConfigAttributes?.unitCap
      )
    : "";

  return (
    <Field.Root
      as="form"
      id={isEditPost ? "ideas-edit-form" : "ideas-create-form"}
      onSubmit={validateAndSubmit}
      w="full"
      alignItems="stretch"
      display="flex"
      flexDirection="column"
      disabled={ideaSubmitting}
      px={4}
      gap={layout === "BudgetAllocator" ? 0 : 6}
      data-testid={isEditPost ? "ideas-edit-form" : "ideas-create-form"}
      css={{
        "& > p": { margin: 0 },
        "& > div": { margin: 0 },
        "& .fr-view": {
          fontSize: "0.875rem",
          fontWeight: "semibold",
          color: "gray.800"
        },
        "& .create-idea-form": {
          fontWeight: "normal"
        },
        "& input, & textarea, & .ehq-dropdown__control": {
          background: "white"
        }
      }}
      /**
       * 'fr-view' is used because of HTMLWrapper
       * TO DO: remove HTMLWrapper implementation for create ideas
       * */
    >
      {isEditPost &&
        hasBackgroundColorNote &&
        !currentPostData?.backgroundColourId &&
        !currentPostData?.mediaUrl && (
          <Alerts
            status="info"
            description={translate("brainstormers.nowChooseBgColourforIdea")}
            onClose={() => setHasBackgroundColorNote(false)}
          />
        )}
      <RequiredField />
      <div className={questionError?.name ? "ehq-question-error" : ""}>
        <SingleLineQuestion
          id="1"
          status={ideaSubmitting ? "saving" : "idle"}
          question={translate("brainstormers.addYourText")}
          required
          hasMaxLength
          maxLength={140}
          notes={translate("common.MaxLengthHelper", {
            count: 140
          })}
          onChangeHandler={handleTitleChange}
          error={questionError?.name}
          existingValue={isEditPost && currentPostData?.name}
        />
      </div>
      {layout === "BudgetAllocator" && (
        <div
          className={
            questionError?.baItemConfigAttributes?.unitCap
              ? "ehq-question-error"
              : ""
          }
        >
          <CommonNumberQuestion
            id="budget"
            gap={0}
            disabled={ideaSubmitting === "saving"}
            questionLabel={translate("questions.budget.label")}
            required
            accessibilityTextOfRequired={`* ${translate("common.required")}`}
            min={0}
            changeEventHandler={(e) => {
              const value = e.target.value.replace(/\D/g, "");

              setPostData({
                ...postData,
                baItemConfigAttributes: { unitCap: value }
              });
              setQuestionError({
                ...questionError,
                baItemConfigAttributes: { unitCap: "" }
              });

              if (!value) {
                setQuestionError({
                  ...questionError,
                  baItemConfigAttributes: { unitCap: "blank" }
                });
              }
            }}
            invalid={questionError?.baItemConfigAttributes?.unitCap}
            errorMessage={
              questionError?.baItemConfigAttributes?.unitCap &&
              translate(
                `errors.${questionError.baItemConfigAttributes.unitCap}`
              )
            }
            answer={budgetAllocatorAnswer}
            questionLabelProps={{ mb: 2 }}
            errorMessageProps={{ mt: 2 }}
            size={{ base: "lg", lg: "md" }}
          />
        </div>
      )}
      <div
        className={
          questionError?.backgroundColourId || questionError?.mediaUrl
            ? "ehq-question-error"
            : ""
        }
      >
        <ColourOrImageQuestion
          id="2"
          required
          status={ideaSubmitting ? "saving" : "idle"}
          question={translate("brainstormers.style")}
          notes={
            allowFileUploadForIdeas
              ? translate("brainstormers.styleNote")
              : translate("brainstormers.styleColourNote")
          }
          backgroundColours={backgroundColours}
          onColourChangeHandler={(colourId: number) => {
            setQuestionError({
              ...questionError,
              backgroundColourId: "",
              mediaUrl: ""
            });
            setPostData({ ...postData, backgroundColourId: colourId });
          }}
          allowImageUpload={allowFileUploadForIdeas}
          onImageChangeHandler={(imageUrl: string) => {
            setQuestionError({
              ...questionError,
              backgroundColourId: "",
              mediaUrl: ""
            });

            if (!imageUrl) {
              setPostData({
                ...postData,
                mediaUrl: "",
                backgroundColourId: ""
              });
              setQuestionError({ ...questionError, mediaUrl: "empty" });
              return;
            }

            setPostData({
              ...postData,
              mediaUrl: imageUrl,
              backgroundColourId: ""
            });
          }}
          error={questionError?.backgroundColourId || questionError?.mediaUrl}
          existingIdOrUrl={
            isEditPost && {
              color: currentPostData?.backgroundColourId,
              image: displayExistingImageUrl
            }
          }
        />
      </div>
      {visibleCategories.length > 0 && (
        <div>
          <CommonDropdownQuestion
            questionId="idea-category"
            placeholder=""
            questionLabel={translate("brainstormers.category")}
            options={categoryOptions}
            answer={selectedCategory}
            changeEventHandler={handleCategoryChange}
            answerAriaLabel={translate("brainstormers.category")}
            isClearable
            disabled={ideaSubmitting === "saving"}
            accessibilityTextOfRequired={`* ${translate("common.required")}`}
            gap={0}
            questionLabelProps={{ mb: 2 }}
            layoutProps={layout === "BudgetAllocator" ? { mt: 4 } : { my: 4 }}
            answerProps={{}}
            size={{ base: "lg", lg: "md" }}
          />
        </div>
      )}
      <div
        data-testid="ideas-create-form-add-more-details"
        className={questionError?.description ? "ehq-question-error" : ""}
      >
        <Field.Root
          w="full"
          my={4}
          id="3"
          disabled={ideaSubmitting === "saving"}
        >
          <Field.Label>{translate("brainstormers.addMoreDetails")}</Field.Label>
          <RichTextEditor
            id="rich-text-editor-description"
            value={
              isEditPost && currentPostData?.description
                ? currentPostData.description
                : postData?.description || ""
            }
            onChange={(html, plainText) => {
              setPostData({
                ...postData,
                description: stripEditorHtml(
                  preserveTrailingSpacesInHtml(html, plainText)
                )
              });
            }}
            placeholder={translate("brainstormers.addMoreDetails")}
            disabled={ideaSubmitting === "saving"}
            allowLinks={isUserAdmin}
            maxLength={limitAdditionalDetailsLength}
            ariaLabel={translate("brainstormers.addMoreDetails")}
            invalid={
              questionError?.description === DESCRIPTION_ERROR_OVER_LIMIT
            }
            errorMessage={
              questionError?.description === DESCRIPTION_ERROR_OVER_LIMIT
                ? translate("errors.overLimit")
                : undefined
            }
          />
        </Field.Root>
      </div>
      {hasErrorInSubmission &&
        !(
          postData?.name &&
          (postData.backgroundColourId || postData.mediaUrl) &&
          (layout !== "BudgetAllocator" ||
            postData?.baItemConfigAttributes?.unitCap)
        ) && (
          <Alerts
            status="error"
            description={translate("errors.pleaseProvideRequiredInfo")}
          />
        )}
      {previewNode()}
    </Field.Root>
  );
};

export default CreateIdea;
